/**
 * useFeedback Hook
 *
 * Provides sound and vibration feedback for parent device events.
 * Audio elements are kept in DOM for reliable playback.
 */

import { useRef, useCallback, useEffect } from "react";

// ============================================================
// Types
// ============================================================

export type FeedbackType =
  | "device_online" // Baby device comes online or state changes
  | "device_offline" // Baby device goes offline
  | "parent_disconnect" // Parent clicks stop while monitoring
  | "baby_disconnect"; // Baby disconnects while parent is monitoring

interface FeedbackConfig {
  soundUrl: string;
  vibrationPattern: number[]; // Vibration pattern in milliseconds
}

// ============================================================
// Configuration
// ============================================================

const FEEDBACK_CONFIG: Record<FeedbackType, FeedbackConfig> = {
  device_online: {
    soundUrl: "/sounds/device_state_change_notif.mp3",
    vibrationPattern: [50], // Light small vibration
  },
  device_offline: {
    soundUrl: "/sounds/baby_offline_notif.mp3",
    vibrationPattern: [50], // Light small vibration
  },
  parent_disconnect: {
    soundUrl: "/sounds/parent_disconnect_notification.mp3",
    vibrationPattern: [50], // Light small vibration
  },
  baby_disconnect: {
    soundUrl: "/sounds/baby_disconnect_notif.mp3",
    vibrationPattern: [100, 100, 100], // Double vibration burst (vibrate-pause-vibrate)
  },
};

// ============================================================
// Hook
// ============================================================

export function useFeedback() {
  // Audio element refs - kept in memory for reliable playback
  const audioElementsRef = useRef<Map<FeedbackType, HTMLAudioElement>>(
    new Map()
  );
  const isInitializedRef = useRef(false);

  // ============================================================
  // Initialize Audio Elements
  // ============================================================

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Create audio elements for each feedback type
    Object.entries(FEEDBACK_CONFIG).forEach(([type, config]) => {
      const audio = new Audio();
      audio.src = config.soundUrl;
      audio.preload = "auto";
      audio.volume = 0.7;

      // Log loading errors
      audio.addEventListener("error", (e) => {
        console.warn(
          `[Feedback] Failed to load sound for ${type}:`,
          audio.src,
          e
        );
      });

      audioElementsRef.current.set(type as FeedbackType, audio);
    });

    console.log("[Feedback] Audio elements initialized");

    // Cleanup on unmount
    return () => {
      audioElementsRef.current.forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
      audioElementsRef.current.clear();
    };
  }, []);

  // ============================================================
  // Vibration Helper
  // ============================================================

  const vibrate = useCallback((pattern: number[]) => {
    // Check if vibration API is supported
    if (!navigator.vibrate) {
      console.log("[Feedback] Vibration API not supported");
      return;
    }

    try {
      navigator.vibrate(pattern);
    } catch (err) {
      console.warn("[Feedback] Vibration failed:", err);
    }
  }, []);

  // ============================================================
  // Play Sound Helper
  // ============================================================

  const playSound = useCallback((type: FeedbackType) => {
    const audio = audioElementsRef.current.get(type);
    if (!audio) {
      console.warn(`[Feedback] No audio element for type: ${type}`);
      return;
    }

    // Reset to beginning if already playing
    audio.currentTime = 0;

    audio.play().catch((err) => {
      // Autoplay might be blocked - this is expected in some cases
      console.warn(`[Feedback] Sound play failed for ${type}:`, err);
    });
  }, []);

  // ============================================================
  // Main Trigger Function
  // ============================================================

  const trigger = useCallback(
    (type: FeedbackType) => {
      const config = FEEDBACK_CONFIG[type];
      if (!config) {
        console.warn(`[Feedback] Unknown feedback type: ${type}`);
        return;
      }

      console.log(`[Feedback] Triggering: ${type}`);

      // Play sound
      playSound(type);

      // Trigger vibration
      vibrate(config.vibrationPattern);
    },
    [playSound, vibrate]
  );

  // ============================================================
  // Return API
  // ============================================================

  return {
    trigger,
    // Expose individual functions for flexibility
    playSound,
    vibrate,
  };
}

// ============================================================
// Default Export
// ============================================================

export default useFeedback;
